/**
 * ExclusiveContentSubscribersService
 *
 * Domain service that fetches one page of the current user's subscribers
 * (i.e. users who are subscribed to me — `meSubscriber == false` in
 * Firestore, since the subscription documents are stored from the viewer's
 * point of view).
 *
 * Distinct from `ExclusiveContentSubscriptionActionsService` (which only
 * handles subscribe/unsubscribe actions) and from the broader
 * `SubscriptionService` used by the older subscriptions screen.
 *
 * Flutter parity:
 *   - lib/presentation/screens/subscriptions/subscribers_screen.dart:75-110
 *     calls `getExclusiveContentSubscriptions(..., false)` to fetch
 *     subscribers (no filtering applied client-side).
 *
 * Architecture:
 *   UseCase → ExclusiveContentSubscribersService → firestoreService
 */

import type { PageCursor } from '@/data/types/json';

import { auth } from '@/data/services/firebase';
import { firestoreService } from '@/data/services/firestoreService';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';
import { ExclusiveContentSubscriptionRemapper } from '@/domain/remappers/exclusiveContentSubscription/exclusiveContentSubscriptionRemapper';

export interface FetchSubscribersPageParams {
  currentUserId: string;
  pageSize: number;
  cursor?: PageCursor;
}

export interface FetchSubscribersPageResult {
  subscribers: ExclusiveContentSubscriptionEntity[];
  cursor?: PageCursor;
  hasMore: boolean;
}

export class ExclusiveContentSubscribersService {
  private static instance: ExclusiveContentSubscribersService;

  private constructor() {}

  static getInstance(): ExclusiveContentSubscribersService {
    if (!this.instance) {
      this.instance = new ExclusiveContentSubscribersService();
    }
    return this.instance;
  }

  /**
   * Fetch one page of subscribers for the current user.
   *
   * Calls firestoreService.getExclusiveContentSubscriptions with
   * `meSubscriber = false` — Flutter parity with
   * subscribers_screen.dart:83-88.
   *
   * Errors propagate to the caller — the hook layer logs via
   * appLogger.error.
   */
  async fetchSubscribersPage(
    params: FetchSubscribersPageParams,
  ): Promise<FetchSubscribersPageResult> {
    const { currentUserId, pageSize, cursor } = params;

    const result = await firestoreService.getExclusiveContentSubscriptions(
      currentUserId,
      cursor,
      pageSize,
      false,
    );

    const dtos = ExclusiveContentSubscriptionRemapper.fromJsonList(result.items);
    const subscribers = ExclusiveContentSubscriptionRemapper.fromDtoList(dtos);

    return {
      subscribers,
      cursor: result.cursor,
      hasMore: result.hasMore,
    };
  }

  /**
   * "Is this peer a subscriber of me?" — checks whether the peer's
   * subscriptions sub-collection contains an active or cancelled subscription
   * pointing at the current user's firebase id.
   *
   * Flutter parity:
   *   - lib/data/data_sources/content_subscriptions_data_source.dart:51-78
   *     (`peerIsSubscribedToExclusiveContent`) — queries
   *     `users/{peerId}/subscriptions where userId == <currentFirebaseId>
   *     AND status in [active, cancelled]` and returns `docs.isNotEmpty`.
   *   - The current Firebase uid is resolved from `auth.currentUser?.uid`
   *     (matching the `auth.currentUser?.uid` convention used by
   *     `walletService.ts:49,83` and other domain services). The data-layer
   *     hop takes the uid as a parameter — `firestoreService.ts` has no
   *     convention for resolving the current user internally.
   *   - When no user is signed in, returns `false` rather than issuing the
   *     query (no signed-in user => no inverse subscription is possible;
   *     equivalent to Flutter's behaviour where the underlying
   *     `AppAuthenticationService.getCurrentFirebaseUser()!.uid` would not
   *     be reachable from a signed-out chat page).
   *
   * @param peerId The peer whose subscriptions sub-collection to inspect.
   * @returns `true` iff the peer has an active or cancelled subscription
   *   to the current user.
   */
  async peerIsSubscribedToCurrentUser(peerId: string): Promise<boolean> {
    const currentFirebaseId = auth.currentUser?.uid;
    if (!currentFirebaseId) {
      return false;
    }
    return firestoreService.peerIsSubscribedToCurrentUser(peerId, currentFirebaseId);
  }
}

// Singleton instance, matching the pattern used by
// ExclusiveContentSubscriptionActionsService.
export const exclusiveContentSubscribersService =
  ExclusiveContentSubscribersService.getInstance();
